import { DialogTrigger } from "@radix-ui/react-dialog";
import { Formik, Form } from "formik";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { FormikCheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { FormInput } from "@/core/components/form-input";
import { SignupSourceSelector } from "@/core/components/signup-source-selector";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { PhoneInput } from "@/core/components/ui/phone-input";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { useBusiness } from "@/core/business";
import { normalizeSignupSource } from "@/core/utils/form";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { ICustomer } from "@/customer/customer";
import { useUpsertCustomer } from "@/customer/query";
import { useGetMyUser } from "@/user/query";

const initialValues = {
  name: "",
  phone_number: "",
  signup_source_id: "none",
  signup_source_type: "",
  onboard: false,
  spiffable: false,
};

interface CustomerFormValues {
  name: string;
  phone_number: string;
  signup_source_id: string;
  signup_source_type: string;
  onboard: boolean;
  spiffable: boolean;
}

interface AddAdvocateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (data: ICustomer) => void;
  onError?: (error: Error) => void;
  trigger?: React.ReactNode;
  businessId?: string;
}

const advocateSchema = Yup.object().shape({
  name: Yup.string().required("Customer name is required"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .matches(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      "Please enter a valid phone number: (XXX) XXX-XXXX",
    )
    .test("is-complete", "Please enter a complete phone number", (value) => {
      if (!value) return false;
      const digitCount = value.replace(/\D/g, "").length;
      return digitCount === 10;
    }),
  signup_source_id: Yup.string(),
  signup_source_type: Yup.string(),
  onboard: Yup.boolean(),
  spiffable: Yup.boolean(),
});

const AddAdvocateModal = ({
  open,
  onOpenChange,
  onSuccess,
  onError,
  trigger,
  businessId,
}: AddAdvocateModalProps) => {
  const { businessId: userBusinessId } = useBusiness();

  const effectiveBusinessId = businessId || (userBusinessId ?? undefined);

  const { mutate: upsertCustomer } = useUpsertCustomer(effectiveBusinessId);
  const { data: currentUser } = useGetMyUser();

  const memoizedInitialValues = useMemo(
    () => ({
      ...initialValues,
      signup_source_id: currentUser?.id || "none",
      signup_source_type: "User",
    }),
    [currentUser?.id],
  );

  const handleSubmit = useCallback(
    async (values: CustomerFormValues) => {
      // Ensure both signup source fields are set or neither is set
      const normalizedSignupSource = normalizeSignupSource(
        values.signup_source_id,
        values.signup_source_type,
      );

      return upsertCustomer(
        {
          name: values.name,
          phone_number: values.phone_number,
          ...normalizedSignupSource,
          status: CUSTOMER_STATUS.ACTIVE,
          onboard: values.onboard,
          spiffable: normalizedSignupSource.signup_source_id
            ? values.spiffable
            : false,
        },
        {
          onSuccess: (data: ICustomer) => {
            toast.success("Advocate created successfully");
            onSuccess?.(data);
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error("Failed to create advocate", {
              description: error.message,
            });
            onError?.(error);
          },
        },
      );
    },
    [upsertCustomer, onSuccess, onError, onOpenChange],
  );

  return (
    <Formik
      initialValues={memoizedInitialValues}
      validationSchema={advocateSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
        resetForm();
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      {({ values, getFieldProps, setFieldValue, isSubmitting, isValid }) => (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogTrigger asChild>{trigger}</DialogTrigger>
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <Form>
              <DialogHeader className="border border-l-0 border-r-0 border-t-0 border-stroke-tertiary">
                <DialogTitle>New Advocate</DialogTitle>
                <DialogDescription>
                  Create a new advocate for your business.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <Stack className="p-6 pb-2 pt-2" gap="lg" fullWidth>
                  <FormInput
                    label="Customer name"
                    {...getFieldProps("name")}
                    fullWidth
                  />
                  <PhoneInput
                    value={values.phone_number}
                    onChange={(value) => setFieldValue("phone_number", value)}
                    label="Phone number"
                    fullWidth
                    required
                    name="phone_number"
                  />
                  <SignupSourceSelector
                    id="signup-source-add-advocate"
                    businessId={effectiveBusinessId}
                    selectedSourceId={values.signup_source_id}
                    selectedSourceType={values.signup_source_type}
                    onSourceChange={(sourceId, sourceType) => {
                      setFieldValue("signup_source_id", sourceId);
                      setFieldValue("signup_source_type", sourceType);
                    }}
                  />
                  <FormikCheckboxWithLabel
                    id="onboard"
                    label="Send advocate program sign-up SMS"
                    checked={values.onboard}
                    onCheckedChange={(checked) =>
                      setFieldValue("onboard", !!checked)
                    }
                  />
                  <FormikCheckboxWithLabel
                    id="spiffable"
                    label="Create spiff for attributed user?"
                    checked={
                      values.signup_source_id === "none" ||
                      !values.signup_source_id
                        ? false
                        : values.spiffable
                    }
                    onCheckedChange={(checked) =>
                      setFieldValue("spiffable", !!checked)
                    }
                    disabled={
                      values.signup_source_id === "none" ||
                      !values.signup_source_id
                    }
                  />
                </Stack>
              </div>

              <DialogFooter>
                <Button
                  variant="tertiary"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                >
                  Create
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  );
};

export default AddAdvocateModal;
